import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, Icon, sonner } from '@sr/ui-library';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import {
  queryOptionsGetUserMe,
  userMutationKeys,
} from '../../../api/permissions/userApi';
import { PageHeader } from '../../../components/PageHeader';
import { MSR3BaseLayout } from '../../../layout/MSR3BaseLayout';
import { userService } from '../../../reducers/permissions/userSlice';
import { userProfileSchema } from '../../../schema/userProfileSchema';
import { useAppDispatch, useTypedSelector } from '../../../stateStore';
import { MSR3ProfileSettings } from './MSR3ProfileSettings';
import {
  EMPTY_PROFILE_FIELDS,
  type ProfileFields,
} from './MSR3ProfileSettingsForm';
import { MSR3_PROFILE_FORM_ID } from './formIds';

const ProfileHeaderActions = ({ isUpdating }: { isUpdating: boolean }) => {
  // `useFormState` subscribes only to the form-state slices we read, so this
  // re-renders far less than a parent-owned observer ever would.
  const { errors, isDirty } = useFormState<ProfileFields>();
  const hasErrors = Object.keys(errors).length > 0;
  const isSaveDisabled = !isDirty || hasErrors || isUpdating;
  // Revert is destructive — gate on `isUpdating` as well as `isDirty` so a
  // mid-flight save can't be clobbered.
  const isRevertDisabled = !isDirty || isUpdating;

  return (
    <>
      <Button
        disabled={isSaveDisabled}
        form={MSR3_PROFILE_FORM_ID}
        loading={isUpdating}
        sectionLeft={<Icon icon="check" />}
        size="sm"
        type="submit"
      >
        <span className="inline sm:hidden">Update</span>
        <span className="hidden sm:inline">Update Details</span>
      </Button>
      <Button
        disabled={isRevertDisabled}
        form={MSR3_PROFILE_FORM_ID}
        sectionLeft={<Icon icon="x" />}
        size="sm"
        type="reset"
        variant="outline"
      >
        <span className="inline sm:hidden">Revert</span>
        <span className="hidden sm:inline">Revert Changes</span>
      </Button>
    </>
  );
};

const ProfileSettingsSkeleton = () => (
  <div
    aria-busy
    aria-live="polite"
    className="flex w-full animate-pulse flex-col gap-5 rounded-lg border border-sr-outline bg-white p-4 shadow-sm sm:p-6"
  >
    <div className="h-4 w-32 rounded bg-sr-outline" />
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div key={idx} className="h-10 rounded bg-sr-outline" />
      ))}
    </div>
  </div>
);

export const MyProfile = () => {
  const dispatch = useAppDispatch();
  const userMe = useTypedSelector(userService.selectors.userMe);
  const userMeQuery = useQuery(queryOptionsGetUserMe());
  const isUpdating =
    useIsMutating({ mutationKey: userMutationKeys.updateProfile() }) > 0;

  /**
   * Two paths can populate `userMe`: the global `userService.findMe()` thunk
   * dispatched from `App.tsx`, and this page's `useQuery`. If react-query
   * wins the race while Redux is still pending we'd otherwise render an
   * empty form (form reads from Redux). Hydrate Redux from the query result
   * so whichever path lands first wins.
   *
   * Follow-up: collapse onto a single source of truth — tracked separately.
   */
  useEffect(() => {
    if (!userMe && userMeQuery.data) {
      dispatch(userService.actions.setUserMe(userMeQuery.data));
    }
  }, [dispatch, userMe, userMeQuery.data]);

  const formMethods = useForm<ProfileFields>({
    // The form is re-seeded inside `MSR3ProfileSettingsForm` once `userMe`
    // arrives. Start with empty so SSR/initial render is stable.
    defaultValues: EMPTY_PROFILE_FIELDS,
    mode: 'onChange',
    resolver: yupResolver(userProfileSchema),
  });

  const isLoading = !userMe && userMeQuery.status === 'pending';
  const hasError = !userMe && userMeQuery.status === 'error';

  return (
    <FormProvider {...formMethods}>
      <MSR3BaseLayout
        pageHeader={
          <PageHeader
            title="My Profile"
            actions={<ProfileHeaderActions isUpdating={isUpdating} />}
          />
        }
        sidebarVariant="profile"
      >
        <div className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold text-sr-black">
            Profile Settings
          </h2>
          {hasError ? (
            <Alert
              actionButtonProps={{
                children: 'Retry',
                onClick: () => {
                  userMeQuery.refetch().catch(() => {
                    sonner.toast.error('Unable to load your profile.');
                  });
                },
              }}
              hasActionButton
              variant="error"
            >
              We couldn&apos;t load your profile. Please try again.
            </Alert>
          ) : isLoading ? (
            <ProfileSettingsSkeleton />
          ) : (
            // Form is mounted only once we have a `userMe`. Avoids the
            // ambiguous "submit with no id ⇒ create-profile" branch firing
            // for existing users when the GET failed.
            <MSR3ProfileSettings />
          )}
        </div>
      </MSR3BaseLayout>
    </FormProvider>
  );
};
